import type { FormFieldOptions } from "@tellescope/types-models";

const DEFAULT_REQUIRED_ADDRESS_KEYS = ["addressLineOne", "city", "state", "zipCode"];

/**
 * The address sub-fields that must be filled in. When a field restricts which parts of an address it
 * collects via `addressFields`, that list defines the requirement — minus `addressLineTwo`, which is
 * always optional.
 */
export const getRequiredAddressKeys = (options: FormFieldOptions | undefined): string[] => {
  const addressFields = options?.addressFields;
  if (addressFields && addressFields.length > 0) return addressFields.filter((f) => f !== "addressLineTwo");
  return DEFAULT_REQUIRED_ADDRESS_KEYS;
};

/**
 * The single ZIP value the address widget edits, recomposed from the stored answer's separate
 * `zipCode` / `zipPlusFour` keys (see `normalizeResponseForSubmission`, which splits them back
 * apart on submit).
 */
export const combineZipCode = (address: unknown): string | undefined => {
  if (typeof address !== "object" || address === null) return undefined;
  const zipCode = Reflect.get(address, "zipCode");
  const zipPlusFour = Reflect.get(address, "zipPlusFour");
  if (typeof zipCode !== "string") return undefined;
  if (typeof zipPlusFour !== "string" || zipPlusFour.length === 0) return zipCode;
  return `${zipCode}-${zipPlusFour}`;
};

/** `fullZIP` requires ZIP+4; otherwise the +4 suffix is optional. */
export const isValidZipCode = (zipCode: string, { fullZIP }: { fullZIP: boolean | undefined }): boolean =>
  fullZIP ? /^\d{5}-\d{4}$/.test(zipCode) : /^\d{5}(-\d{4})?$/.test(zipCode);
